import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#064E3B]"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Order not found</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-8 text-center" data-testid="order-confirmation">
          <div className="flex justify-center mb-6">
            <div className="bg-green-100 rounded-full p-4">
              <CheckCircle className="w-16 h-16 text-green-600" data-testid="success-icon" />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3" data-testid="confirmation-title">
            Order Placed Successfully!
          </h1>

          <p className="text-lg text-zinc-600 mb-8">
            Thank you for your purchase. Your order has been received.
          </p>

          <div className="bg-[#F8FAFC] rounded-lg p-6 mb-8 text-left" data-testid="order-details">
            <h2 className="text-xl font-semibold text-[#0f172a] mb-4">Order Details</h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-600">Order ID:</span>
                <span className="font-semibold text-[#0f172a]" data-testid="order-id">{order.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Total Amount:</span>
                <span className="font-semibold text-[#064E3B]" data-testid="order-total">₹{order.total_amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Payment Method:</span>
                <span className="font-semibold text-[#0f172a]" data-testid="payment-method">{order.payment_method.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Status:</span>
                <span className="font-semibold text-[#0f172a] capitalize" data-testid="order-status">{order.order_status}</span>
              </div>
            </div>
          </div>

          <div className="bg-[#FEF3C7] border border-[#FDE047] rounded-lg p-6 mb-8 text-left">
            <div className="flex items-start gap-3">
              <Package className="w-6 h-6 text-[#F59E0B] mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-[#92400E] mb-2">What's Next?</h3>
                <ul className="text-sm text-[#92400E] space-y-1">
                  <li>• You will receive order confirmation via email and SMS</li>
                  <li>• We'll notify you once your order ships</li>
                  <li>• Expected delivery: 5-7 business days</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              data-testid="continue-shopping-button"
              onClick={() => navigate('/')}
              className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold rounded-full py-4 text-lg"
            >
              <Home className="w-5 h-5 mr-2" />
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
